import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  Paper,
  CircularProgress,
  TextField,
} from "@mui/material";
import { api } from "../api";
import PaginationControls from "./PaginationControls";
import usePageTitle from "../hooks/usePageTitle";

export default function AccountsSearchPage() {
  usePageTitle("Collectors");
  const location = useLocation();
  const navigate = useNavigate();

  const q = useMemo(() => new URLSearchParams(location.search).get("q") || "", [location.search]);
  const [input, setInput] = useState(q);
  useEffect(() => setInput(q), [q]);

  const [page, setPage] = useState(1);
  const pageSize = 12;

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [friendBusy, setFriendBusy] = useState({});

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page]);

  useEffect(() => {
    let aborted = false;
    const run = async () => {
      setLoading(true);
      try {
        const url = q.trim()
          ? `/api/accounts?q=${encodeURIComponent(q)}&page=${page}&pageSize=${pageSize}`
          : `/api/accounts?all=1&page=${page}&pageSize=${pageSize}`;

        const res = await api.get(url);
        const data = res.data || { results: [], total: 0 };

        const list = Array.isArray(data) ? data : (data.results || []);
        const count = typeof data.total === "number" ? data.total : list.length;

        if (!aborted) {
          setResults(list);
          setTotal(count);
        }
      } catch (e) {
        if (!aborted) {
          setResults([]);
          setTotal(0);
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    run();
    return () => { aborted = true; };
  }, [q, page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const onSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    navigate(`/accounts${input ? `?q=${encodeURIComponent(input)}` : ""}`);
  };

  const formatItemCountLine = (count, scope) => {
    const numericCount = Number(count);
    if (!Number.isFinite(numericCount)) return "";
    return `${numericCount} item${numericCount === 1 ? "" : "s"} in ${scope}`;
  };

  const updateCollectorFriendship = (userId, patch) => {
    setResults((current) => current.map((collector) => (
      Number(collector.user_id) === Number(userId) ? { ...collector, ...patch } : collector
    )));
  };

  const handleFriendAction = async (collector) => {
    const userId = Number(collector?.user_id);
    const status = collector?.friendship_status || null;
    const friendshipId = Number(collector?.friendship_id);
    const hasFriendshipId = Number.isFinite(friendshipId) && friendshipId > 0;
    if (
      !Number.isFinite(userId) ||
      status === "pending_sent" ||
      status === "accepted" ||
      status === "self" ||
      (status === "pending_received" && !hasFriendshipId)
    ) return;

    setFriendBusy((current) => ({ ...current, [userId]: true }));
    try {
      if (status === "pending_received") {
        const { data } = await api.post("/api/friends/accept", { friendship_id: friendshipId });
        updateCollectorFriendship(userId, {
          friendship_status: data?.friendship_status || "accepted",
          friendship_id: data?.friendship_id ?? friendshipId,
        });
      } else {
        const { data } = await api.post("/api/friends/request", { addressee_user_id: userId });
        updateCollectorFriendship(userId, {
          friendship_status: data?.friendship_status || "pending_sent",
          friendship_id: data?.friendship_id ?? collector.friendship_id,
        });
      }
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setFriendBusy((current) => ({ ...current, [userId]: false }));
    }
  };

  const getFriendButton = (collector) => {
    const status = collector?.friendship_status || null;
    const friendshipId = Number(collector?.friendship_id);
    const hasFriendshipId = Number.isFinite(friendshipId) && friendshipId > 0;
    if (status === "self") return { label: "You", disabled: true };
    if (status === "pending_sent") return { label: "Request Sent", disabled: true };
    if (status === "accepted") return { label: "Friends", disabled: true };
    if (status === "pending_received") {
      return { label: hasFriendshipId ? "Accept" : "Pending", disabled: !hasFriendshipId };
    }
    return { label: "Add Friend", disabled: false };
  };

  const heading = loading
    ? (q.trim() ? "Searching..." : "Loading all public collectors...")
    : q.trim()
      ? `${total} ${total === 1 ? "collector" : "collectors"} for '${q}'`
      : `${total} ${total === 1 ? "public collector" : "public collectors"} total`;

  return (
    <Box>
      <Box sx={{ p: 3, pb: "60px", maxWidth: 1200, mx: "auto" }}>
        <Paper sx={{
          p: 2,
          mb: 4,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "2fr auto" },
          gap: 2,
          alignItems: "center",
        }}>
          <form onSubmit={onSubmit} style={{ display: "flex", gap: 8 }}>
            <TextField
              fullWidth
              placeholder="Search collectors by username..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <Button type="submit" variant="contained">Search</Button>
          </form>
          <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
            <Typography component="h2" variant="subtitle1">{heading}</Typography>
          </Box>
        </Paper>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : results.length === 0 ? (
          <Typography>No collectors found.</Typography>
        ) : (
          <>
            <Grid container spacing={3}>
              {results.map((acc) => {
                const username = acc.username ?? acc.name ?? "";
                const avatar = acc.avatar_url || acc.profile_picture || "/logos/default-avatar.jpg";
                const friendButton = getFriendButton(acc);
                const isFriendBusy = Boolean(friendBusy[acc.user_id]);
                return (
                  <Grid item xs={12} sm={6} md={4} key={`${acc.user_id}-${username}`}>
                    <Card sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={avatar}
                        alt={username}
                        sx={{ objectFit: "contain", backgroundColor: "#f8f8f8" }}
                      />
                      <CardContent>
                        <Typography variant="h6">{username ? `@${username}` : "-"}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {acc.display_name || acc.name || "-"}
                        </Typography>
                        {formatItemCountLine(acc.item_count, "collection") && (
                          <Typography variant="body2" color="text.secondary">
                            {formatItemCountLine(acc.item_count, "collection")}
                          </Typography>
                        )}
                        {Number.isFinite(Number(acc.wishlist_count)) && (
                          <Typography variant="body2" color="text.secondary">
                            {formatItemCountLine(acc.wishlist_count, "wishlist")}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions
                        sx={{
                          mt: "auto",
                          px: 2,
                          pb: 2,
                          display: "flex",
                          flexDirection: { xs: "column", sm: "row" },
                          gap: 1,
                          justifyContent: { xs: "stretch", sm: "flex-end" },
                        }}
                      >
                        <Button
                          component={Link}
                          to={`/mycollection/${encodeURIComponent(username)}`}
                          variant="contained"
                          size="small"
                          sx={{ width: { xs: "100%", sm: "auto" } }}
                        >
                          View Collection
                        </Button>
                        <Button
                          component={Link}
                          to={`/wishlist/${encodeURIComponent(username)}`}
                          variant="outlined"
                          color="secondary"
                          size="small"
                          sx={{ width: { xs: "100%", sm: "auto" } }}
                        >
                          View Wishlist
                        </Button>
                        <Button
                          variant="outlined"
                          color="inherit"
                          size="small"
                          disabled={friendButton.disabled || isFriendBusy}
                          onClick={() => handleFriendAction(acc)}
                          sx={{ width: { xs: "100%", sm: "auto" } }}
                        >
                          {isFriendBusy ? "Saving..." : friendButton.label}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </>
        )}
      </Box>
    </Box>
  );
}
